/**
 * Quantities as integer thousandths.
 *
 * Plants sell in fractional trays, so the data model stores `numeric(14,3)`.
 * Carrying that as a JavaScript float would put 0.1 + 0.2 into a picked
 * quantity and, through the price calculation, into an invoice. Quantities are
 * therefore integer thousandths of a unit, and the decimal point exists only at
 * the parse and format boundaries.
 *
 * See docs/02-data-model.md, "Conventions on every synced table".
 */
import { err, ok } from './result.js';
export const QUANTITY_SCALE = 1000;
/**
 * `numeric(14,3)` allows 11 integer digits and 3 decimals. In thousandths that
 * is 99_999_999_999_999, comfortably inside Number.MAX_SAFE_INTEGER, so exact
 * integer arithmetic holds across the whole representable range.
 */
export const MAX_QUANTITY_THOUSANDTHS = 99_999_999_999_999;
/**
 * A refusal, as a sentence somebody at a keyboard can act on.
 *
 * Beside the error type rather than at the screen, because a quantity is typed
 * in four places already and will be typed on a dispatch bench next - and four
 * screens each phrasing "at most three decimal places" their own way is four
 * chances to phrase it wrongly.
 */
export function describeQuantityError(error) {
    switch (error.kind) {
        case 'not-a-number':
            return 'That is not a quantity. Write it as a number, like 24 or 2.5.';
        case 'too-many-decimals':
            return `A quantity has at most ${String(error.max)} decimal places.`;
        case 'out-of-range':
            return 'That quantity is larger than this system can hold.';
        case 'grouped-digits':
            return 'Write a quantity without spaces or thousands separators: 1250, not 1 250.';
    }
}
export const ZERO_QUANTITY = 0;
/** Build a quantity from an exact count of thousandths. */
export function quantityFromThousandths(thousandths) {
    if (!Number.isSafeInteger(thousandths)) {
        throw new TypeError(`quantity thousandths must be a safe integer, got ${thousandths}`);
    }
    if (Math.abs(thousandths) > MAX_QUANTITY_THOUSANDTHS) {
        throw new RangeError(`quantity ${thousandths} exceeds numeric(14,3)`);
    }
    return thousandths;
}
/** Build a quantity from a whole number of units. */
export function quantityFromUnits(units) {
    if (!Number.isInteger(units)) {
        throw new TypeError(`quantityFromUnits expects a whole number, got ${units}. Use parseQuantity for fractional input.`);
    }
    return quantityFromThousandths(units * QUANTITY_SCALE);
}
export function thousandthsOf(quantity) {
    return quantity;
}
/**
 * Parse a decimal string. Deliberately strict: no thousands separators, no
 * currency symbols, no exponent notation, at most three decimal places.
 *
 * Migration input from Touchpoint will not always look like this. That is the
 * migration's problem to normalise and report on (T-MIGRATE-03 requires dirty
 * records to be rejected into a report, never silently coerced), so the domain
 * parser stays narrow rather than guessing what a human meant.
 */
export function parseQuantity(input) {
    const trimmed = input.trim();
    if (/[\s,]/.test(trimmed)) {
        return err({ kind: 'grouped-digits', input });
    }
    const match = /^([+-]?)(\d+)(?:\.(\d+))?$/.exec(trimmed);
    if (!match) {
        return err({ kind: 'not-a-number', input });
    }
    const [, sign = '', whole = '', fraction] = match;
    if (fraction !== undefined && fraction.length > 3) {
        return err({ kind: 'too-many-decimals', input, max: 3 });
    }
    const padded = (fraction ?? '').padEnd(3, '0');
    const magnitude = Number(whole) * QUANTITY_SCALE + Number(padded);
    if (!Number.isSafeInteger(magnitude) || magnitude > MAX_QUANTITY_THOUSANDTHS) {
        return err({ kind: 'out-of-range', input });
    }
    return ok((sign === '-' ? -magnitude : magnitude));
}
export function addQuantity(a, b) {
    return quantityFromThousandths(a + b);
}
export function subtractQuantity(a, b) {
    return quantityFromThousandths(a - b);
}
export function sumQuantity(quantities) {
    return quantities.reduce(addQuantity, ZERO_QUANTITY);
}
export function compareQuantity(a, b) {
    if (a < b)
        return -1;
    if (a > b)
        return 1;
    return 0;
}
export function isZeroQuantity(quantity) {
    return quantity === 0;
}
export function isNegativeQuantity(quantity) {
    return quantity < 0;
}
/** Fixed three-decimal form, matching how the value is stored. */
export function formatQuantityExact(quantity) {
    const negative = quantity < 0;
    const magnitude = Math.abs(quantity);
    const whole = Math.trunc(magnitude / QUANTITY_SCALE);
    const fraction = magnitude % QUANTITY_SCALE;
    return `${negative ? '-' : ''}${String(whole)}.${String(fraction).padStart(3, '0')}`;
}
/**
 * Display form with trailing zeros removed: 12 rather than 12.000, 1.5 rather
 * than 1.500. A picking slip reading "12.000 trays" invites a misread.
 */
export function formatQuantity(quantity) {
    const exact = formatQuantityExact(quantity);
    return exact.includes('.') ? exact.replace(/\.?0+$/, '') : exact;
}
//# sourceMappingURL=quantity.js.map